import type { TFunction } from 'react-i18next';

import type {
  AddMobileErrorResponse,
  SendOtpErrorResponse,
  VerifyOtpErrorResponse,
} from 'types/phoneVerification';
import { ErrorWithCode } from 'types/utils';

import { errorHasACode } from './error';
import { getBlockedTime } from './numbers';

const getRequestOtpServerErrorMessage = (error: unknown, t: TFunction<'errors'[]>) => {
  if (errorHasACode<SendOtpErrorResponse | AddMobileErrorResponse>(error)) {
    switch (error.code) {
      case 'mobile_otp_blocked': {
        const { context } = error;
        const { time } = context;
        const { unit, value } = getBlockedTime(time);
        const errorMessage = t('errors:user.mobile_otp_blocked_time', {
          unit,
          value,
        });
        return errorMessage;
      }
      case 'duplicate_phone_number_found':
        return t('errors:user.duplicate_phone_number');

      case 'invalid_phone_number_format':
        return t('errors:user.invalid_phone_number_format');

      case 'already_verified':
        return t('errors:user.already_verified');

      case 'blocked_country_calling_code':
        return t('errors:user.blocked_country_calling_code');

      case 'failed_to_generate_or_send_otp':
        return t('errors:user.failed_to_generate_or_send_otp');

      case 'duplicate_phone_number_found_in_deleted_account':
        return t('errors:user.duplicate_phone_number_found_in_deleted_account');

      default:
        return t('errors:utils.somethingWentWrong');
    }
  }
  return t('errors:utils.somethingWentWrong');
};

const getVerifyOtpServerErrorMessage = (
  errorObj: VerifyOtpErrorResponse | ErrorWithCode | null,
  t: TFunction<'errors'[]>
) => {
  if (!errorObj?.code || !errorObj?.context) return t('errors:utils.somethingWentWrong');
  const { code, context } = errorObj;

  if (code === 'invalid_otp') {
    const { count = 0, max_attempts: maxAttempts = 5 } = context;
    const attemptsRemaining = maxAttempts - count;
    let errorMsg = t('errors:custom.invalid_otp_with_count_plural', {
      count: attemptsRemaining,
    });
    if (attemptsRemaining === 1) errorMsg = t('errors:custom.invalid_otp_with_count');
    return errorMsg;
  }

  if (code === 'failed_to_validate_otp') {
    const { count = 0, max_attempts: maxAttempts = 5 } = context;
    const attemptsRemaining = maxAttempts - count;
    let errorMsg = t('errors:user.failed_to_validate_otp', {
      count: attemptsRemaining,
    });
    if (attemptsRemaining === 1) errorMsg = t('errors:custom.invalid_otp_with_count');
    return errorMsg;
  }

  if (code === 'verify_phone_number_blocked') {
    const { value, unit } = getBlockedTime(context.time);
    return t('errors:user.verify_phone_number_blocked', {
      blockTime: `${value} ${unit}`,
    });
  }

  return t('errors:utils.somethingWentWrong');
};

export { getRequestOtpServerErrorMessage, getVerifyOtpServerErrorMessage };
